(() => {
  const MIN_SWIPE_DISTANCE_PX = 10;

  const idSuffixByInfix = new Map();
  const generateElementId = infix => {
    const nextSuffix = idSuffixByInfix.get(infix) ?? 0;
    idSuffixByInfix.set(infix, nextSuffix + 1);
    return `carousel-controls-${infix}-${nextSuffix}`;
  };

  /**
   * A custom element that wraps a container element to turn it into a carousel.
   *
   * The wrapped DOM must include elements with the following class names:
   * - `carousel-controls__indicators-container` -- An empty `<div>`. Must precede the
   *   `.carousel-controls__viewer-container` element as its immediate sibling.
   * - `carousel-controls__viewer-container` -- A `<div>` containing the elements listed below. Must
   *   follow the `.carousel-controls__indicators-container` element as its immediate sibling.
   * - `carousel-controls__rotate-to-previous` -- The `<button>` for rotating to the previous item.
   *   Must be a direct child of the `.carousel-controls__viewer-container` element.
   * - `carousel-controls__items-list` -- The container element containing the carousel's items.
   *   Must be a direct child of the `.carousel-controls__viewer-container` element.
   * - `carousel-controls__rotate-to-next` -- The `<button>` for rotating to the next item.
   *   Must be a direct child of the `.carousel-controls__viewer-container` element.
   *
   * `<carousel-controls>` supports the following attributes:
   * - `active-media-query` -- An optional media query to control when the carousel controls are
   *   active. E.g. `active-media-query="(max-width: 749px)"`. When the carousel controls are
   *   *inactive*, then no rotate buttons or item indicators are displayed. Omit this attribute or
   *   set it to the empty string if you want the carousel controls to be active on all
   *   devices/screens.
   * - `center-line-for-rotate-buttons` -- An optional query selector to specify a child element of
   *   the selected item to use as the reference point for vertically positioning the rotate
   *   buttons. The rotate buttons will be positioned so that their vertical center aligns with
   *   the vertical center of this element. Omit or set to the empty string to center the rotate
   *   buttons vertically within the entire selected item.
   */
  class CarouselControls extends HTMLElement {
    static observedAttributes = ['active-media-query', 'center-line-for-rotate-buttons'];

    constructor() {
      super();
      this._itemIndex = 0;

      this._mediaQuery = null;
      this._onMediaQueryChange = () => this._updateCarouselState();

      this._mutationObserver = null;
      this._mutationObserverCallback = () => {
        this._renderIndicators();
        this._updateCarouselState();
      };

      this._onImagesListTouchStart = (startEvent) => {
        if (!this._areControlsActive) return;

        // Swipe handling adpated from https://stackoverflow.com/a/23230280
        const { clientX: startX, clientY: startY } = startEvent.touches[0];
        const onTouchMove = (moveEvent) => {
          const { clientX: currentX, clientY: currentY } = moveEvent.touches[0];
          const isHorizontal = Math.abs(currentX - startX) > Math.abs(currentY - startY);
          if (isHorizontal && Math.abs(currentX - startX) > MIN_SWIPE_DISTANCE_PX) {
            // Swiping to the right means selecting the PREVIOUS image and vice versa.
            this.itemIndex += (currentX > startX ? -1 : 1);
            onTouchEnd();
          }
        };
        const onTouchEnd = () => {
          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', onTouchEnd);
        };
        document.addEventListener('touchmove', onTouchMove);
        document.addEventListener('touchend', onTouchEnd);
      };
    }

    get _items() {
      return this._itemsList?.children ?? new HTMLCollection();
    }

    get _indicators() {
      return this._indicatorsContainer?.children ?? new HTMLCollection();
    }

    get _areControlsActive() {
      return this._items.length > 1 && (this._mediaQuery?.matches ?? true);
    }

    /**
     * Get the 0-based index of the currently selected item.
     */
    get itemIndex() {
      return this._itemIndex;
    }

    /**
     * Set the 0-based index of the currently selected item. Indices outside of the range from 0
     * inclusive to the number of items exclusive are rotated so as to be in that range. E.g. -1
     * selects the last item.
     */
    set itemIndex(itemIndex) {
      this._itemIndex = itemIndex;
      this._updateCarouselState();
    }

    /**
     * Re-position the various controls inside this carousel. Call this if a carousel that was
     * hidden is becoming visible.
     */
    // TODO: This method is unfortunate. It's mainly about positioning the rotate buttons. See TODO
    // comment re _positionRotateButtons below.
    repositionControls() {
      this._positionRotateButtons();
    }

    _setupMediaQuery() {
      this._disposeMediaQuery();
      this._mediaQuery = matchMedia(this.getAttribute('active-media-query') || '');
      this._mediaQuery.addEventListener('change', this._onMediaQueryChange);
    }

    _disposeMediaQuery() {
      this._mediaQuery?.removeEventListener('change', this._onMediaQueryChange);
      this._mediaQuery = null;
    }

    _setupMutationObserver() {
      this._disposeMutationObserver();
      this._mutationObserver = new MutationObserver(this._mutationObserverCallback);
      this._mutationObserver.observe(this._itemsList, { childList: true });
    }

    _disposeMutationObserver() {
      this._mutationObserver?.disconnect();
      this._mutationObserver = null;
    }

    _buildIndicator(index) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'carousel-controls__indicator';
      button.addEventListener('click', () => {
        this.itemIndex = index;
      });
      button.tabIndex = 0;
      return button;
    }

    _renderIndicators() {
      this._indicatorsContainer.replaceChildren(
        ...[...this._items].map((_, index) => this._buildIndicator(index)),
      );
    }

    // TODO: Find a better way to vertically align the rotate buttons with something OTHER than the
    // center of the .carousel-controls__viewer-container element. The implementation here is very
    // timing-dependent.
    _positionRotateButtons() {
      let marginTop = 0;
      let marginBottom = 0;
      const centerLineQuery = this.getAttribute('center-line-for-rotate-buttons');
      if (centerLineQuery && this._areControlsActive) {
        const sampleItem = [...this._items].find(
          item => !item.matches('.carousel-controls__item--hidden')
        );
        const centerLineElement = sampleItem?.querySelector(centerLineQuery);
        if (centerLineElement) {
          // Position the rotate buttons so they're vertically centered around the center of the
          // center line element.
          const { bottom: itemBottom, top: itemTop } = sampleItem.getBoundingClientRect();
          const {
            bottom: centerLineBottom,
            top: centerLineTop,
          } = centerLineElement.getBoundingClientRect();
          marginTop = itemTop - centerLineTop;
          marginBottom = itemBottom - centerLineBottom;
        }
      }
      this.style.setProperty('--rotate-button-margin-top', `${marginTop}px`);
      this.style.setProperty('--rotate-button-margin-bottom', `${marginBottom}px`);
    }

    _updateCarouselState() {
      this.classList.toggle('carousel-controls--inactive', !this._areControlsActive);
      this.classList.toggle('carousel-controls--active', this._areControlsActive);
      this._itemIndex = this._areControlsActive && this._items.length > 0 ?
        this._itemIndex % this._items.length :
        0;
      if (this._itemIndex < 0) {
        this._itemIndex = this._items.length + this._itemIndex;
      }
      [...this._items].forEach((item, index) => {
        item.classList.toggle(
          'carousel-controls__item--hidden',
          this._areControlsActive && index !== this._itemIndex,
        );
      });
      [...this._indicators].forEach((indicator, index) => {
        indicator.classList.toggle(
          'carousel-controls__indicator--active',
          this._areControlsActive && index === this._itemIndex,
        );
      });
      this._positionRotateButtons();
      this._setAriaAttributes();
    }

    _setCarouselAriaAttributes() {
      // See https://www.w3.org/WAI/ARIA/apg/patterns/carousel/ and 
      // https://www.smashingmagazine.com/2023/02/guide-building-accessible-carousels/#the-carousel-container
      this.role = 'region';
      this.ariaRoleDescription = this._areControlsActive ? 'carousel' : '';
    }

    _setItemAriaAttributes() {
      [...this._items].forEach((item) => {
        // See https://www.w3.org/WAI/ARIA/apg/patterns/carousel/ and
        // https://www.smashingmagazine.com/2023/02/guide-building-accessible-carousels/#the-slide-container.
        // We're following the "grouped carousel" pattern.
        item.role = 'group';
        item.ariaRoleDescription = 'slide';
        item.ariaHidden = `${item.matches('.carousel-controls__item--hidden')}`;
        if (!item.id) {
          item.id = generateElementId('item');
        }
        if (!item.getAttribute('aria-labelledby')) {
          const itemLabel = item.querySelector('.carousel-controls__item-label');
          if (itemLabel) {
            if (!itemLabel.id) {
              itemLabel.id = generateElementId('item-label');
            }
            item.setAttribute('aria-labelledby', itemLabel.id);
          }
        }
      });
    }

    _setIndicatorAriaAttributes() {
      // See https://www.w3.org/WAI/ARIA/apg/patterns/carousel/ and
      // https://www.smashingmagazine.com/2023/02/guide-building-accessible-carousels/#the-controls
      // We're following the "grouped carousel" pattern.
      this._indicatorsContainer.role = 'group';
      [...this._indicators].forEach((indicator, index) => {
        indicator.ariaDisabled = `${indicator.matches('.carousel-controls__indicator--active')}`;
        indicator.setAttribute('aria-labelledby', this._items[index]?.id || '');
      });
    }

    _setAriaAttributes() {
      this._setCarouselAriaAttributes();
      this._setItemAriaAttributes();
      this._setIndicatorAriaAttributes();
    }

    connectedCallback() {
      this._itemsList = this.querySelector('.carousel-controls__items-list');
      this._previousButton = this.querySelector('.carousel-controls__rotate-to-previous');
      this._nextButton = this.querySelector('.carousel-controls__rotate-to-next');
      this._indicatorsContainer = this.querySelector('.carousel-controls__indicators-container');

      [this._previousButton, this._nextButton].forEach((button) => {
        button.classList.add('carousel-controls__rotate-button');
        button.tabIndex = 0;
      });
      [
        this._previousButton,
        this._nextButton,
        this._indicatorsContainer,
      ].forEach((control) => {
        control.classList.add('carousel-controls__control');
      });

      this._previousButton.addEventListener('click', () => {
        this.itemIndex -= 1;
      });
      this._nextButton.addEventListener('click', () => {
        this.itemIndex += 1;
      });

      this._itemsList.addEventListener('touchstart', this._onImagesListTouchStart);

      this._setupMediaQuery();
      this._setupMutationObserver();
      this._renderIndicators();
      this._updateCarouselState();
      this._hasPerformedConnectedCallback = true;
    }

    disconnectedCallback() {
      this._hasPerformedConnectedCallback = false;
      this._itemsList?.removeEventListener('touchstart', this._onImagesListTouchStart);
      this._disposeMutationObserver();
      this._disposeMediaQuery();
    }

    attributeChangedCallback() {
      if (!this._hasPerformedConnectedCallback) return;

      this._setupMediaQuery();
      this._updateCarouselState();
    }
  }

  if (!customElements.get('carousel-controls')) {
    customElements.define('carousel-controls', CarouselControls);
  }
})();
